<!-- ════════════════════════════════════════════════════
     SUPER ADMIN → Admin Dashboard
     ════════════════════════════════════════════════════ -->
<ng-container *ngIf="is_super_admin && gs.user_data?.user_type !== 'student'">

  <div class="flex flex-col overflow-hidden" style="height: calc(100vh - 32px)">

    <app-header [show_back]="false" label="Dashboard"></app-header>

    <div class="bg-gray-50 flex flex-col gap-4 px-5 py-4 overflow-hidden flex-1 min-h-0">

      <!-- Filter Bar -->
      <div class="flex items-center gap-3 flex-wrap">
        <div class="min-w-44 flex-1">
          <app-select-input placeholder="University" label="University" [options]="ds.dropdown_list?.university_list" [(ngModel)]="university_id" (ngModelChange)="on_university_change()">
          </app-select-input>
        </div>
        <div class="min-w-36 flex-1">
          <app-select-input placeholder="Academic Year" label="Academic Year" [options]="filtered_academic_years" [(ngModel)]="academic_year_id" (ngModelChange)="on_academic_year_change()">
          </app-select-input>
        </div>
        <div class="min-w-44 flex-1">
          <app-select-input placeholder="Exam Name" label="Exam Name" [options]="filtered_exams" [(ngModel)]="exam_id" (ngModelChange)="on_exam_change()">
          </app-select-input>
        </div>
        <div class="min-w-44 flex-1">
          <app-select-input placeholder="Subject Name" label="Subject Name" [options]="filtered_subjects" [(ngModel)]="subject_id" (ngModelChange)="on_subject_change()">
          </app-select-input>
        </div>
        <div class="w-px h-9 bg-gray-300 mx-1 self-center flex-shrink-0"></div>
        <div class="min-w-44 flex-1">
          <app-select-input placeholder="Evaluation" label="Evaluation" [options]="evaluation_type_options" [(ngModel)]="evaluation_type_filter" (ngModelChange)="on_evaluation_filter_change()">
          </app-select-input>
        </div>
        <div class="w-px h-9 bg-gray-300 mx-1 self-center flex-shrink-0"></div>
        <div class="min-w-44 flex-1">
          <app-select-input *ngIf="overview_type === 'evaluator'" placeholder="All Evaluators" label="Evaluator" [options]="filtered_evaluator_list" [(ngModel)]="faculty_id" (ngModelChange)="load_dashboard()">
          </app-select-input>
          <app-select-input *ngIf="overview_type === 'head_evaluator'" placeholder="All Head Evaluators" label="Head Evaluator" [options]="filtered_head_evaluator_list" [(ngModel)]="faculty_id" (ngModelChange)="load_dashboard()">
          </app-select-input>
        </div>
      </div>

      <!-- Loading spinner -->
      <div *ngIf="is_loading" class="flex justify-center items-center py-20">
        <div class="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>

      <ng-container *ngIf="!is_loading">

        <!-- Tabs + Stats row -->
        <div class="flex items-center justify-between flex-wrap gap-3">
          <!-- Tabs -->
          <div class="flex items-center rounded-xl border border-gray-200">
            <button (click)="set_overview_type('evaluator')" class="px-6 py-2 text-sm font-medium tracking-wide transition-all rounded-xl" [class.border]="overview_type === 'evaluator'" [class.text-gray-400]="overview_type !== 'evaluator'" [style.background-color]="overview_type === 'evaluator' ? '#3F41D11A' : ''" [style.border-color]="overview_type === 'evaluator' ? '#3F41D1' : ''" [style.color]="overview_type === 'evaluator' ? '#3F41D1' : ''">
              EVALUATOR
            </button>
            <div class="w-px h-5 bg-gray-300 mx-1"></div>
            <button (click)="set_overview_type('head_evaluator')" class="px-6 py-2 text-sm font-medium tracking-wide transition-all rounded-xl" [class.border]="overview_type === 'head_evaluator'" [class.text-gray-400]="overview_type !== 'head_evaluator'" [style.background-color]="overview_type === 'head_evaluator' ? '#3F41D11A' : ''" [style.border-color]="overview_type === 'head_evaluator' ? '#3F41D1' : ''" [style.color]="overview_type === 'head_evaluator' ? '#3F41D1' : ''">
              HEAD EVALUATOR
            </button>
          </div>
          <!-- Stats pills -->
          <div *ngIf="script_overview" class="flex items-center gap-3">
            <div class="flex items-center gap-3 bg-white rounded-2xl px-6 py-3" style="box-shadow: -3px 6px 20px rgba(0,0,0,0.12)">
              <span class="text-sm text-[#212121]">Total {{ overview_type === 'head_evaluator' ? 'Head Evaluators' : 'Evaluators' }}:</span>
              <span class="text-sm font-medium text-[#212121]">{{ total_faculty_count }}</span>
            </div>
            <div class="flex items-center gap-3 bg-white rounded-2xl px-6 py-3" style="box-shadow: -3px 6px 20px rgba(0,0,0,0.12)">
              <span class="text-sm text-[#212121]">System Avg Time / Script:</span>
              <span class="text-sm font-medium text-[#212121]">{{ format_overview_time(script_overview.avg_time_per_script) }}</span>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <app-empty-state *ngIf="!script_overview" title="No Data Found" description="No assessments match the selected filters." minHeight="300px">
        </app-empty-state>

        <!-- Main two-column layout -->
        <ng-container *ngIf="script_overview">
          <div class="flex items-stretch bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex-1 min-h-0">

            <!-- ── Left column: 5 cards + donut chart ── -->
            <div class="flex flex-col gap-4 flex-shrink-0 p-5 bg-white overflow-y-auto" style="width: 44%">

              <!-- Stat cards -->
              <div class="flex flex-col gap-3">
                <!-- Row 1 -->
                <div class="grid grid-cols-3 gap-3">
                  <!-- Scanned Scripts / Re-Eval Requests -->
                  <div class="bg-white rounded-2xl border border-gray-100 border-t-[6px] p-5 flex flex-col gap-3 shadow-sm h-36" style="border-top-color: #F5B995;">
                    <div class="w-12 h-12 rounded-xl flex items-center justify-center" style="background-color: #F5B99530;">
                      <i class='bx bx-file text-2xl' style="color: #D79B77;"></i>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span class="text-3xl font-medium text-gray-500">{{ script_overview.total_scanned }}</span>
                      <span class="text-sm text-[#212121]">{{ evaluation_type_filter === 'Re-Evaluation' ? 'Re-Eval Requests' : 'Scanned Scripts' }}</span>
                    </div>
                  </div>
                  <!-- Assigned / HE to Check -->
                  <div class="bg-white rounded-2xl border border-gray-100 border-t-[6px] p-5 flex flex-col gap-3 shadow-sm h-36" style="border-top-color: #AFCFF5;">
                    <div class="w-12 h-12 rounded-xl flex items-center justify-center" style="background-color: #AFCFF530;">
                      <i class='bx bx-clipboard text-2xl' style="color: #91B1D7;"></i>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span class="text-3xl font-medium text-gray-500">
                        {{ overview_type === 'head_evaluator' ? script_overview.he_to_check_count : script_overview.total_assigned }}
                      </span>
                      <span class="text-sm text-[#212121]">
                        <ng-container *ngIf="evaluation_type_filter === 'Re-Evaluation'">
                          {{ overview_type === 'head_evaluator' ? 'HE Re-Eval' : 'Assigned' }}
                        </ng-container>
                        <ng-container *ngIf="evaluation_type_filter !== 'Re-Evaluation'">
                          {{ overview_type === 'head_evaluator' ? 'HE to Check (' + script_overview.he_pct + '%)' : 'Assigned Scripts' }}
                        </ng-container>
                      </span>
                    </div>
                  </div>
                  <!-- Evaluated / Checked -->
                  <div class="bg-white rounded-2xl border border-gray-100 border-t-[6px] p-5 flex flex-col gap-3 shadow-sm h-36" style="border-top-color: #F2DE8A;">
                    <div class="w-12 h-12 rounded-xl flex items-center justify-center" style="background-color: #F2DE8A30;">
                      <i class='bx bx-check-square text-2xl' style="color: #CAB662;"></i>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span class="text-3xl font-medium text-gray-500">
                        {{ overview_type === 'head_evaluator' ? script_overview.total_checked : script_overview.total_evaluated }}
                      </span>
                      <span class="text-sm text-[#212121]">
                        <ng-container *ngIf="evaluation_type_filter === 'Re-Evaluation'">
                          {{ overview_type === 'head_evaluator' ? 'HE Completed' : 'Completed' }}
                        </ng-container>
                        <ng-container *ngIf="evaluation_type_filter !== 'Re-Evaluation'">
                          {{ overview_type === 'head_evaluator' ? 'Checked' : 'Evaluated' }}
                        </ng-container>
                      </span>
                    </div>
                  </div>
                </div>
                <!-- Row 2 -->
                <div class="flex gap-3 justify-center">
                  <!-- Pending -->
                  <div class="bg-white rounded-2xl border border-gray-100 border-t-[6px] p-5 flex flex-col gap-3 shadow-sm h-36" style="width: calc((100% - 24px) / 3); border-top-color: #9FD8B6;">
                    <div class="w-12 h-12 rounded-xl flex items-center justify-center" style="background-color: #9FD8B630;">
                      <i class='bx bx-time-five text-2xl' style="color: #77B08E;"></i>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span class="text-3xl font-medium text-gray-500">{{ script_overview.total_pending }}</span>
                      <span class="text-sm text-[#212121]">Pending</span>
                    </div>
                  </div>
                  <!-- Exceptions -->
                  <div class="bg-white rounded-2xl border border-gray-100 border-t-[6px] p-5 flex flex-col gap-3 shadow-sm h-36" style="width: calc((100% - 24px) / 3); border-top-color: #CBB8F5;">
                    <div class="w-12 h-12 rounded-xl flex items-center justify-center" style="background-color: #CBB8F530;">
                      <i class='bx bx-error text-2xl' style="color: #A390CD;"></i>
                    </div>
                    <div class="flex flex-col gap-1">
                      <span class="text-3xl font-medium text-gray-500">{{ script_overview.total_exceptions }}</span>
                      <span class="text-sm text-[#212121]">Exceptions</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Donut chart (no card wrapper) -->
              <ng-container *ngIf="pie_chart_data.length > 0; else no_chart">
                <div class="flex flex-col items-center gap-3">
                  <svg class="w-full" viewBox="-3 -3 226 226" style="max-height: 320px;">
                    <g *ngFor="let slice of pie_chart_data">
                      <path [attr.d]="slice.path" [attr.fill]="slice.color" [attr.stroke]="slice.border_color" stroke-width="1"></path>
                      <text *ngIf="+slice.pct >= 5" [attr.x]="slice.lx" [attr.y]="slice.ly" text-anchor="middle" dominant-baseline="middle" font-size="11" font-weight="500" fill="#374151">{{ slice.pct }}%</text>
                    </g>
                    <circle cx="110" cy="110" r="30" fill="white"></circle>
                  </svg>
                </div>
              </ng-container>
              <ng-template #no_chart>
                <div class="flex flex-col items-center gap-2 text-gray-400 py-10">
                  <i class='bx bx-pie-chart-alt-2 text-5xl'></i>
                  <span class="text-sm">No chart data available</span>
                </div>
              </ng-template>

            </div>

            <!-- Vertical divider -->
            <div class="w-px bg-gray-200 flex-shrink-0"></div>

            <!-- ── Right column: evaluator table ── -->
            <div class="flex-1 bg-white flex flex-col min-h-0 border-r-4 border-r-indigo-500">
              <div class="px-5 py-4 border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
                <span class="text-base font-medium text-[#212121]">{{ evaluation_type_filter === 'Re-Evaluation' ? 'Re-Evaluation Report' : 'Evaluation Report' }}</span>
                <button *ngIf="faculty_overview.length > 0" (click)="download_report()" class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors" title="Download Report">
                  <i class='bx bx-download text-lg'></i>
                  <span>Download</span>
                </button>
              </div>
              <div class="overflow-y-auto flex-1 min-h-0">
                <table class="w-full text-sm">
                  <thead class="sticky top-0 bg-white border-b border-gray-100 z-10">
                    <tr>
                      <th class="text-left px-5 py-3 text-indigo-500 font-medium">{{ overview_type === 'head_evaluator' ? 'Head Evaluator' : 'Evaluator' }}</th>
                      <th class="text-center px-4 py-3 text-indigo-500 font-medium">{{ evaluation_type_filter === 'Re-Evaluation' ? 'Assigned' : 'Assigned Script' }}</th>
                      <th class="text-center px-4 py-3 text-indigo-500 font-medium">{{ evaluation_type_filter === 'Re-Evaluation' ? 'Completed' : 'Evaluated Script' }}</th>
                      <th class="text-center px-4 py-3 text-indigo-500 font-medium">Average Time/Script</th>
                      <th class="text-center px-4 py-3 text-indigo-500 font-medium">Efficiency</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let row of faculty_overview" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td class="px-5 py-3.5 text-gray-500 font-medium">{{ row.name }}</td>
                      <td class="px-4 py-3.5 text-center text-gray-600">{{ row.assigned_scripts }}</td>
                      <td class="px-4 py-3.5 text-center text-gray-600">{{ row.evaluated_scripts }}</td>
                      <td class="px-4 py-3.5 text-center text-gray-700">{{ format_time(row.avg_time_per_script) }}</td>
                      <td class="px-4 py-3.5 text-center">
                        <span class="font-medium text-sm" [class.text-green-600]="row.efficiency >= 70" [class.text-red-500]="row.efficiency < 70">
                          {{ row.efficiency }}%
                        </span>
                      </td>
                    </tr>
                    <tr *ngIf="faculty_overview.length === 0">
                      <td colspan="5" class="px-5 py-10 text-center text-gray-400">No faculty data available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </ng-container>

      </ng-container>
    </div>

  </div>

</ng-container>

<!-- ════════════════════════════════════════════════════
     STUDENT → Blank Dashboard
     ════════════════════════════════════════════════════ -->
<ng-container *ngIf="gs.user_data?.user_type === 'student'">
  <app-header [show_back]="false" label="Dashboard"></app-header>
  <app-empty-state title="No Records found" description="" minHeight="calc(100vh - 100px)"></app-empty-state>
</ng-container>

<!-- ════════════════════════════════════════════════════
     OTHER USERS → Evaluator / Head-Evaluator Dashboard
     ════════════════════════════════════════════════════ -->
<ng-container *ngIf="!is_super_admin && gs.user_data?.user_type !== 'student'">

  <app-header [show_back]="false" label="Evaluation & Re-evluation"></app-header>

  <div class="p-1 w-full bg-gray-50">
    @if (gs.user_role_data.evaluations_and_revaluations_evaluation_view || gs.user_role_data.evaluations_and_revaluations_head_evaluation_view) {
    <div class="flex flex-col gap-5">
      <div class="flex items-center justify-between gap-5">
        <div class="flex items-center justify-start gap-5">
          <app-select-input label="Evaluation Type" class="min-w-72" [is_required]="true" [options]="[{title:'Evaluation',value: 'Evaluation'},{title:'Re-Evaluation',value: 'Re-Evaluation'}]" [(ngModel)]="evaluation_type" (ngModelChange)="applyFilter()"></app-select-input>
          <app-select-input label="Academic Year" class="min-w-72" [is_required]="true" [options]="ds.dropdown_list?.academic_list" [(ngModel)]="academic_year" (ngModelChange)="applyFilter()"></app-select-input>
        </div>
        <app-search-input #searchbar placeholder="Search Subject, Exam" (valueChange)="applySearch($event)"></app-search-input>
      </div>

      <app-empty-state *ngIf="subjects.length === 0" title="No Assessments Found" description="  No assessments are available at this moment. Please contact the admin." minHeight="50vh">
      </app-empty-state>

      <div *ngIf="subjects.length > 0" class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 w-full gap-4">
        <div *ngFor="let item of subjects" class="bg-white rounded-lg w-full shadow-sm border border-gray-100 p-4 flex flex-col gap-4">

          <div class="flex justify-between items-center">
            <div class="flex flex-col gap-2">
              <span class="text-[20px] font-medium leading-[100%] tracking-[0.02em] capitalize text-[#212121] ">{{ item?.Subject?.name }}</span>
              <p class="text-[14px] font-normal leading-[100%] tracking-[0.02em] capitalize text-gray-500">{{ item?.Exam?.name }}</p>
            </div>
            <span *ngIf="!gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view" class="flex items-center h-[31px] gap-2 px-4 py-2 rounded-[64px]  bg-indigo-50 text-indigo-600 border border-indigo-100  font-medium text-[14px] leading-[100%] tracking-[0.02em] capitalize whitespace-nowrap">
              {{ (evaluation_type === 'Re-Evaluation' ? 'Re-Evaluation Level 1' : 'Evaluation Level ' + (item?.current_evaluation_level || 1)) }}
            </span>
          </div>

          <div class="flex flex-nowrap gap-2">
            <button (click)="openPdf(item.qp_file)" [ngClass]="{'text-[#06C270] bg-[rgba(6,194,112,0.07)] border-[#06C270]': item.qp_file != '', 'text-red-500 bg-red-50 border-red-500': item.qp_file == ''}" class="h-[28px] flex items-center justify-center gap-2 p-2 w-full text-nowrap rounded border text-[11px] font-medium shadow-[inset_2px_2px_8px_rgba(0,0,0,0.2)]">Question Paper</button>
            <button (click)="openPdf(item.soe_upload)" [ngClass]="{'text-[#06C270] bg-[rgba(6,194,112,0.07)] border-[#06C270]': item.soe_upload != '', 'text-red-500 bg-red-50 border-red-500': item.soe_upload == ''}" class="h-[28px] flex items-center justify-center gap-2 p-2 w-full  rounded border text-[11px] font-medium shadow-[inset_2px_2px_8px_rgba(0,0,0,0.2)] ">SOE</button>
            <button (click)="openPdf(item.model_answer_sheet)" [ngClass]="{'text-[#06C270] bg-[rgba(6,194,112,0.07)] border-[#06C270]': item.model_answer_sheet != '', 'text-red-500 bg-red-50 border-red-500': item.model_answer_sheet == ''}" class="h-[28px] flex items-center justify-center gap-2 p-2 w-full text-nowrap rounded border text-[11px] font-medium shadow-[inset_2px_2px_8px_rgba(0,0,0,0.2)]">Model Answer Script</button>
            <button [ngClass]="{'text-[#06C270] bg-[rgba(6,194,112,0.07)] border-[#06C270]': item.annotations_required, 'text-red-500 bg-red-50 border-red-500': !item.annotations_required}" class="h-[28px] flex items-center justify-center gap-2 p-2 w-full  rounded border text-[11px] font-medium shadow-[inset_2px_2px_8px_rgba(0,0,0,0.2)]">Annotations</button>
          </div>

          <div class="grid grid-cols-2 border rounded-xl divide-x divide-y overflow-hidden border-gray-200">
            <div class="p-4 flex justify-between items-center">
              <span *ngIf="gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view" class="truncate text-[14px] font-medium leading-[100%] tracking-[0.02em] capitalize text-[#212121]">Target</span>
              <span *ngIf="gs.user_role_data?.evaluations_and_revaluations_evaluation_view" class="truncate text-[14px] font-medium leading-[100%] tracking-[0.02em] capitalize text-[#212121]">Target / Day</span>
              <span *ngIf="gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view" class="text-[14px] font-normal text-gray-600">{{ item?.target }}</span>
              <span *ngIf="!gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view" class="text-[14px] font-normal text-gray-600">{{ item?.per_day_target }}</span>
            </div>
            <div class="p-4 flex justify-between items-center ">
              <span class="truncate text-[14px] font-medium leading-[100%] tracking-[0.02em] capitalize text-[#212121]">Min Time / Script</span>
              <span class="text-[14px] font-normal text-gray-600">{{ item?.min_time_per_answer_script }}</span>
            </div>
            <div class="p-4 flex justify-between items-center ">
              <span class="truncate text-[14px] font-medium leading-[100%] tracking-[0.02em] capitalize text-[#212121]">Grace Period</span>
              <span class="text-[14px] font-normal text-gray-600">{{ item.grace_period }}</span>
            </div>
            <div class="p-4 flex justify-between items-center">
              <span class="truncate text-[14px] font-medium leading-[100%] tracking-[0.02em] capitalize text-[#212121]">Evaluated Today</span>
              <span class="text-[14px] font-normal text-gray-600">{{ item.evaluated_today || 0}}</span>
            </div>
          </div>

          <div class="flex items-center gap-2 text-gray-700 font-medium">
            <img class="h-[18px] w-[18px]" src="../../../assets/CalendarDots.png" alt="">
            <span class="text-[#212121] text-[13px] font-medium leading-[100%] tracking-[0.02em] capitalize">Start Date &rarr; End Date:</span>
            <span class=" text-[13px] font-normal leading-[100%] tracking-[0.02em] capitalize text-gray-600 ml-1">{{ item?.evaluation_start_date | date:'dd-MM-yyyy' }} &rarr; {{
              item?.evaluation_end_date | date:'dd-MM-yyyy' }}</span>
          </div>
          <div class="w-full h-[2px] bg-gray-200 rounded-full"></div>

          <div [ngClass]="{'grid-cols-4': gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view}" class="grid grid-cols-3 gap-3">
            <div class="h-[61px] flex flex-col justify-center items-center text-center gap-2 px-4 py-2 bg-[#EBEBEB] border border-[rgba(0,0,0,0.12)] rounded-lg">
              <span class="text-[20px] font-medium leading-[100%] tracking-[0.02em] capitalize text-gray-500">{{ item?.total_script}}</span>
              <span class="text-[14px] font-normal leading-[100%] tracking-[0.02em] capitalize">Total</span>
            </div>
            <div class="h-[61px] flex flex-col items-center justify-center gap-2 px-4 py-2 text-center bg-[#E1E8F5] border border-[rgba(0,0,0,0.12)] rounded-lg">
              <span class="text-[20px] font-medium leading-[100%] tracking-[0.02em] capitalize text-gray-500">{{ item?.evaluated_script}}</span>
              <span class="text-[14px] font-normal leading-[100%] tracking-[0.02em] capitalize">Evaluated</span>
            </div>
            <div *ngIf="gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view" class="h-[61px] flex flex-col items-center justify-center text-center gap-2 px-4 py-2 bg-[#EBFBF4]   border border-[rgba(0,0,0,0.12)]   rounded-lg">
              <span class="text-[20px] font-medium leading-[100%] tracking-[0.02em] capitalize text-gray-500">{{ item?.checked_scripts || 0}}</span>
              <span class="text-[14px] font-normal leading-[100%] tracking-[0.02em] capitalize">Checked</span>
            </div>
            <div class="h-[61px] flex flex-col justify-center items-center text-center gap-2 px-4 py-2 bg-[rgba(209,126,63,0.2)] border border-[rgba(0,0,0,0.12)] rounded-lg">
              <span class="text-[20px] font-medium leading-[100%] tracking-[0.02em] capitalize text-gray-500">{{ gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view ? (item?.he_pending_scripts || 0) : item?.pending_script }}</span>
              <span class="text-[14px] font-normal leading-[100%] tracking-[0.02em] capitalize">Pending</span>
            </div>
          </div>

          <button routerLink="/evaluation/script/list" [queryParams]="{assessment_id: item.id, dashboard: true, evaluation_type : this.params.evaluation_type}" class="h-[40px] flex items-center justify-center gap-2 px-4 py-2 bg-[#3F41D1] hover:bg-indigo-700 text-white rounded-[64px]  font-medium text-[14px] leading-[100%] tracking-[0.02em] capitalize">
            Script Evaluation
          </button>

        </div>
      </div>
    </div>
    }

    @if (this.gs.users.role == 'Head Evaluator') {
    <div class="p-4 w-full">
      <app-evaluator-list-page class="w-full" [show_heder]="false"></app-evaluator-list-page>
    </div>
    }
  </div>

</ng-container>